import { ReactNode } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    boxShadow: 'none',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '14px',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: '#FFFFFF',
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableHead-root': {
      backgroundColor: 'rgba(96, 165, 250, 0.28)',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      padding: '16px 20px',
      verticalAlign: 'middle',
      backgroundClip: 'padding-box',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      padding: '16px 20px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      fontSize: '0.92rem',
      fontWeight: 500,
      letterSpacing: '0.02rem',
      lineHeight: 1.35,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: 'rgba(96, 165, 250, 0.09)',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.22),
    },
    '& .MuiTablePagination-root': {
      borderTop: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: 'rgba(96, 165, 250, 0.12)',
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: 64,
      paddingLeft: 20,
      paddingRight: 12,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontSize: '0.78rem',
      fontWeight: 600,
      letterSpacing: '0.08rem',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
    },
    '& .MuiTablePagination-actions .MuiIconButton-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiTablePagination-actions .Mui-disabled': {
      color: theme.palette.action.disabled,
    },
  },
  headerLabel: {
    display: 'inline-block',
    fontSize: '0.78rem',
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: '0.14rem',
    textTransform: 'uppercase',
    color: theme.palette.text.primary,
  },
  emptyStateCell: {
    paddingTop: 36,
    paddingBottom: 36,
    fontSize: '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.05rem',
    color: theme.palette.text.secondary,
  },
}));

type StyledTableColumn = {
  label: ReactNode;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
};

type StyledTableProps = {
  columns: StyledTableColumn[];
  children: ReactNode;
  hasRows: boolean;
  emptyMessage: string;
  loadingMessage?: string;
  isLoading?: boolean;
  pagination?: ReactNode;
  emptyColSpan?: number;
};

export default function StyledTable({
  columns,
  children,
  hasRows,
  emptyMessage,
  loadingMessage = 'Loading...',
  isLoading = false,
  pagination,
  emptyColSpan,
}: StyledTableProps) {
  const { classes } = useStyles();

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={String(column.label)} align={column.align}>
                <Typography component="span" className={classes.headerLabel}>
                  {column.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {hasRows ? (
            children
          ) : (
            <TableRow>
              <TableCell colSpan={emptyColSpan ?? columns.length} align="center" className={classes.emptyStateCell}>
                {isLoading ? loadingMessage : emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination}
    </TableContainer>
  );
}